import _ from "lodash";
import { connect } from "react-redux";
import moment from "moment";
import React, { useState } from "react";

import { isUserAdmin } from "@/utils/authToken";

import { ALL_STATES } from "../../../constants/states";
import Button from "../../UI/Button";
import DatePicker from "../../UI/DatePicker";
import { getAutomationDataAttr } from "../../../utils/automationUtils";
import images from "../../../utils/images";
import { Portal } from "../../Common/Modal/Portal";

import ProfileDetailsCard from "./ProfileDetailsCard";

const convertYyyyMmDdToMmDdYyyy = (str) => {
  const [year, month, day] = str.split("-");

  return `${month}/${day}/${year}`;
};

const isLicensedExpired = (mmddyyyyDate) => {
  const date = moment(mmddyyyyDate, "MM/DD/YYYY");

  return moment() >= date;
};

const maybeExclamationGlyphicon = (mmddyyyyDate) => {
  if (isLicensedExpired(mmddyyyyDate)) {
    return (
      <>
        <span className="glyphicon glyphicon-exclamation-sign"></span>
      </>
    );
  }

  return;
};

const redTextIfExpired = (mmddyyyyDate) => {
  if (isLicensedExpired(mmddyyyyDate)) {
    return { color: "red" };
  }

  return {};
};

const makeStateInDropdown = (state, testId) => {
  return (
    <option
      key={state.display}
      {...getAutomationDataAttr(`${testId}${state.display.replaceAll(" ", "")}`)}
    >
      {state.display}
    </option>
  );
};

const datepickerModal = (index, state, props) => {
  const {
    commitDate,
    dateContext,
    displayDatepicker,
    markDatepickerAsClosed,
    toggleDatepicker,
    updateStateLicenseExpirationFromDatePicker,
  } = props;

  return (
    <Portal
      headerRender={<b>{`Please Select Expiration Date for ${state} License`}</b>}
      isBottomCloseActive={false}
      isOpenOnRender={false}
      onModalClose={markDatepickerAsClosed(index)}
      openButtonRender={
        <img alt="message" src={images("./icons/common/schedule/schedule-ter-none-2.svg")} />
      }
      testId={`StateToPractice${index}SetExpirationDate`}
      triggerModalStateChange={displayDatepicker}
    >
      <DatePicker
        externalCtx={dateContext}
        loadingSlots={false}
        onDaySelect={(date) =>
          updateStateLicenseExpirationFromDatePicker(index, convertYyyyMmDdToMmDdYyyy(date))
        }
      />
      <Button
        className="Btn Btn--pri Btn--sm-2 workflow-btn float-right"
        type="submit"
        {...getAutomationDataAttr(`StateToPractice${index}ConfirmExpirationDate`)}
        onClick={() => {
          commitDate(index);
          toggleDatepicker(index);
        }}
      >
        Confirm Date
      </Button>
    </Portal>
  );
};

const makeStateRow = (stateOfPractice, index, props) => {
  const { displayDatepicker, expirationDate, licenseNumber, state } = stateOfPractice;

  const {
    commitDate,
    dateContext,
    deleteStateLicense,
    markDatepickerAsClosed,
    toggleDatepicker,
    updateStateLicenseExpirationFromDatePicker,
    updateStateLicenseNumber,
    updateStateValue,
  } = props;

  const datepickerProps = {
    commitDate,
    dateContext,
    displayDatepicker,
    markDatepickerAsClosed,
    toggleDatepicker,
    updateStateLicenseExpirationFromDatePicker,
  };
  const testId = `StateToPractice${index}`;
  const selectTestId = `${testId}Select`;

  return (
    <>
      <div className="row" key={`stateLicense-${index}`}>
        <div className="col-md-3">
          <label>
            <b>State</b>
          </label>
          <select
            className="form-control"
            key={`state-${index}`}
            value={state}
            {...getAutomationDataAttr(selectTestId)}
            onChange={updateStateValue(index)}
          >
            {ALL_STATES.map((s) => makeStateInDropdown(s, selectTestId))}
          </select>
        </div>
        <div className="col-md-3">
          <label>
            <b>License Number</b>
          </label>
          <input
            className="form-control"
            onChange={updateStateLicenseNumber(index)}
            placeholder="Enter license number"
            value={licenseNumber}
            {...getAutomationDataAttr(`${testId}LicenseNumber`)}
          />
        </div>
        <div className="col-md-3">
          <label style={redTextIfExpired(expirationDate)}>
            <b>Expiration Date</b>
            {maybeExclamationGlyphicon(expirationDate)}
          </label>
          <input
            className="form-control"
            readOnly
            style={redTextIfExpired(expirationDate)}
            value={expirationDate}
          />
        </div>
        <div className="col-md-2">
          <br />
          {datepickerModal(index, state, datepickerProps)}
        </div>
        <div className="col-md-1">
          <div
            className="mx-auto mt-3 d-flex align-item-center cursor tw:justify-center"
            style={{ "padding-top": "12px" }}
          >
            <img
              onClick={deleteStateLicense(index)}
              {...getAutomationDataAttr(`${testId}DeleteLicense`)}
              alt=""
              src={images("./icons/delete-trash.svg")}
            />
          </div>
        </div>
      </div>
      <br />
    </>
  );
};

const UpdateStateArea = (props) => {
  const {
    commitDate,
    dateContext,
    deleteStateLicense,
    markDatepickerAsClosed,
    states_to_practice,
    toggleDatepicker,
    updateStateLicenseExpirationFromDatePicker,
    updateStateLicenseNumber,
    updateStateValue,
  } = props;

  const makeStateRowProps = {
    commitDate,
    dateContext,
    deleteStateLicense,
    markDatepickerAsClosed,
    toggleDatepicker,
    updateStateLicenseExpirationFromDatePicker,
    updateStateLicenseNumber,
    updateStateValue,
  };

  if (_.isNil(states_to_practice)) {
    return;
  }

  return (
    <>
      {states_to_practice.map((stateOfPractice, index) =>
        makeStateRow(stateOfPractice, index, makeStateRowProps),
      )}
    </>
  );
};

const makeSingleStateDisplayArea = (state) => {
  if (_.isNil(state)) {
    return;
  }
  const licenseNumberToDisplay = state.licenseNumber || "";
  const expirationToDisplay = state.expirationDate || "";

  return (
    <div className="col-md-6">
      {/* Indentation madness */}
      <div className="row" style={{ fontWeight: "bold", paddingLeft: "5px" }}>
        {`• ${state.state}`}
      </div>
      <div className="row">
        <div className="col-md-6">{`License Number: ${licenseNumberToDisplay}`}</div>
        <div className="col-md-6" style={redTextIfExpired(state.expirationDate)}>
          {`Exp: ${expirationToDisplay}`}
        </div>
      </div>
      <br />
    </div>
  );
};

const makeStateDisplayRow = (leftColumnState, rightColumnState, index) => {
  return (
    <div
      className="row"
      key={`row-index-${index}`}
      {...getAutomationDataAttr(`AuthorizedStateToPracticeRow${index}`)}
    >
      {makeSingleStateDisplayArea(leftColumnState)}
      {makeSingleStateDisplayArea(rightColumnState)}
    </div>
  );
};

const licensedStateDisplayArea = (states) => {
  if (_.isNil(states) || (_.isEmpty(states) && isUserAdmin())) {
    return (
      <div className="ProviderProfile__BoldPrimarySubtext">
        Add Authorized States with the Edit Button
      </div>
    );
  }

  // Design calls for rows of 2 (left and right)
  // This function breaks the states into a structure representing the row
  // Example [{stateData}, {stateData}, {stateData}] => [[{stateData}, {stateData}], [{stateData}]]
  const rowData = _.chunk(states, 2);

  return (
    <>
      {rowData.map((data, index) => {
        const [leftColumnState, rightColumnState] = data;

        return makeStateDisplayRow(leftColumnState, rightColumnState, index);
      })}
    </>
  );
};

/**
 * Resets the Datepicker visibility
 * The Portal element is designed to be visible when an attribute is true and not visible
 * when in null | false state
 * For historical reasons, the DatePicker must instantiate with null and not false
 * In a complex workflow such this component with lists of Datepickers, to prevent
 * prior datepickers from being rendered on a re-render, it is easier to set all datepickers
 * to closed via null attribute
 * This approach will be used any time a datepicker from the list of datepickers (embedded in a modal)
 * are invoked or interacted with
 */
const resetDatepickerClosed = (stateToPractice) => {
  const clonedStateToPractice = _.cloneDeep(stateToPractice);

  clonedStateToPractice["displayDatepicker"] = null;

  return clonedStateToPractice;
};

const StatesToPractice = (props) => {
  const {
    commitAndSubmitStateLicensureDataToServer,
    dateContext,
    flags,
    showUpdateStateModal,
    states_to_practice,
    toggleStateModal,
  } = props;

  const [uncommitedStatesToPractice, setUncommitedStatesToPractice] = useState(
    _.cloneDeep(states_to_practice),
  );

  const updateStateValue = (index) => (e) => {
    const newUncommitedStatesToPractice = _.cloneDeep(uncommitedStatesToPractice);

    newUncommitedStatesToPractice[index].state = e.target.value;
    setUncommitedStatesToPractice(newUncommitedStatesToPractice);
  };

  const addAuthorizedState = () => {
    const newState = {
      displayDatepicker: null,
      expirationDate: null,
      licenseNumber: null,
      state: "New York",
    };

    if (_.isNil(uncommitedStatesToPractice) || _.isEmpty(uncommitedStatesToPractice)) {
      setUncommitedStatesToPractice([newState]);
    } else {
      // Slightly odd edge case: make sure every node has a visibility setting (this is not stored in db)
      const cleanStatesToPractice = _.cloneDeep(uncommitedStatesToPractice).map((stateToPractice) =>
        resetDatepickerClosed(stateToPractice),
      );

      // If the list already exists, create a new with the the placeholder newState on the end
      cleanStatesToPractice.push(newState);
      setUncommitedStatesToPractice(cleanStatesToPractice);
    }
  };

  const updateStateLicenseNumber = (index) => (e) => {
    const newUncommitedStatesToPractice = _.cloneDeep(uncommitedStatesToPractice);

    newUncommitedStatesToPractice[index].licenseNumber = e.target.value;
    setUncommitedStatesToPractice(newUncommitedStatesToPractice);
  };

  const markDatepickerAsClosed = (index) => () => {
    const newUncommitedStatesToPractice = _.cloneDeep(uncommitedStatesToPractice);

    newUncommitedStatesToPractice[index]["displayDatepicker"] = null;
    setUncommitedStatesToPractice(newUncommitedStatesToPractice);
  };

  // Required as someone can bail from the modal and not "commit" the datepicker changes
  const updateStateLicenseExpirationFromDatePicker = (index, value) => {
    const newUncommitedStatesToPractice = _.cloneDeep(uncommitedStatesToPractice);

    newUncommitedStatesToPractice[index].dateToCommit = value;
    setUncommitedStatesToPractice(newUncommitedStatesToPractice);
  };

  const commitDate = (index) => {
    const newUncommitedStatesToPractice = [...uncommitedStatesToPractice];

    newUncommitedStatesToPractice[index].expirationDate =
      newUncommitedStatesToPractice[index].dateToCommit;
    setUncommitedStatesToPractice(newUncommitedStatesToPractice);
  };

  const toggleDatepicker = (index) => {
    // Only toggle the specific display attribute of the stateOfPractice (selected by index)
    const cleanStatesToPractice = _.cloneDeep(uncommitedStatesToPractice).map((stateToPractice) =>
      resetDatepickerClosed(stateToPractice),
    );
    const newStateOfInterest = cleanStatesToPractice[index];

    // Reverse the state of the datepicker for that state (hide/show modal for that state datepicker)
    newStateOfInterest["displayDatepicker"] = !newStateOfInterest["displayDatepicker"];
    // Set updated element in list
    cleanStatesToPractice[index] = newStateOfInterest;
    setUncommitedStatesToPractice(cleanStatesToPractice);
  };

  const deleteStateLicense = (index) => (e) => {
    const cleanStatesToPractice = _.cloneDeep(uncommitedStatesToPractice)
      .map((stateToPractice) => resetDatepickerClosed(stateToPractice))
      .filter((_stateLicenseData, listIndex) => listIndex !== index);

    setUncommitedStatesToPractice(cleanStatesToPractice);
  };

  const updateStateAreaProps = {
    commitDate,
    dateContext,
    deleteStateLicense,
    markDatepickerAsClosed,
    states_to_practice: uncommitedStatesToPractice,
    toggleDatepicker,
    updateStateLicenseExpirationFromDatePicker,
    updateStateLicenseNumber,
    updateStateValue,
  };

  return (
    <>
      {isUserAdmin() && (
        <ProfileDetailsCard
          heading="Authorized States to Practice"
          isEditable
          onclick={() => toggleStateModal()}
          testId="AuthorizedStatesToPractice"
        >
          <div className="ProviderProfile__states-to-practice my-2 pt-4 px-2">
            <div>
              <ul className="ProviderProfile__insurance-info-list fs-14">
                <Portal
                  headerRender={
                    <div className="fs-22 mb-5 text-center tw:font-bold">
                      Authorized States to Practice
                    </div>
                  }
                  isBottomCloseActive={false}
                  isOpenOnRender={false}
                  onModalClose={() => setUncommitedStatesToPractice(states_to_practice)}
                  triggerModalStateChange={showUpdateStateModal}
                >
                  <UpdateStateArea {...updateStateAreaProps} />
                  <br />
                  <div className="row">
                    <div className="col-md-12">
                      <Button
                        className="Btn Btn--otl-gry Btn--sm-2 workflow-btn float-left"
                        type="button"
                        {...getAutomationDataAttr("AuthorizedStatesToPracticeAddStateButton")}
                        onClick={() => addAuthorizedState()}
                      >
                        Add State
                      </Button>
                      <Button
                        className="Btn Btn--pri Btn--sm-2 workflow-btn float-right"
                        type="submit"
                        {...getAutomationDataAttr("AuthorizedStatesToPracticeSaveButton")}
                        onClick={() => {
                          commitAndSubmitStateLicensureDataToServer(uncommitedStatesToPractice);
                        }}
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                </Portal>
                {props.isEditable && licensedStateDisplayArea(uncommitedStatesToPractice)}
              </ul>
            </div>
          </div>
        </ProfileDetailsCard>
      )}

      {!isUserAdmin() && (
        <ProfileDetailsCard
          edit={props.isEditable}
          heading="Authorized States to Practice"
          onclick={() => toggleStateModal()}
          testId="AuthorizedStatesToPractice"
        >
          <div className="ProviderProfile__states-to-practice my-2 pt-4 px-2">
            <div>
              <ul className="ProviderProfile__insurance-info-list fs-14">
                <Portal
                  headerRender={
                    <div className="fs-22 mb-5 text-center tw:font-bold">
                      Authorized States to Practice
                    </div>
                  }
                  isBottomCloseActive={false}
                  isOpenOnRender={false}
                  onModalClose={() => setUncommitedStatesToPractice(states_to_practice)}
                  triggerModalStateChange={showUpdateStateModal}
                >
                  <UpdateStateArea {...updateStateAreaProps} />
                  <br />
                  <div className="row">
                    <div className="col-md-12">
                      <Button
                        className="Btn Btn--otl-gry Btn--sm-2 workflow-btn float-left"
                        onClick={() => addAuthorizedState()}
                        type="button"
                      >
                        Add State
                      </Button>
                      <Button
                        className="Btn Btn--pri Btn--sm-2 workflow-btn float-right"
                        onClick={() => {
                          commitAndSubmitStateLicensureDataToServer(uncommitedStatesToPractice);
                        }}
                        type="submit"
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                </Portal>
                {props.isEditable && licensedStateDisplayArea(uncommitedStatesToPractice)}
                {props.isEditable && (
                  <p className="ProviderProfile__toUpdateMessage">
                    To update authorized states, contact{" "}
                    <a href="mailto:support@aptihealth.com">support@aptihealth.com</a>.
                  </p>
                )}
              </ul>
            </div>
          </div>
        </ProfileDetailsCard>
      )}
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    flags: state?.flags,
  };
};

export default connect(mapStateToProps)(StatesToPractice);
